import { useEffect, useState } from 'react';
import isEqual from 'react-fast-compare';

import { styled } from '../../../../stitches.config';
import { Pagination } from '../../../controls/Pagination/Pagination';
import { Button } from '../../../controls/Button/Button';
import { Checkbox } from '../../../controls/Checkbox/Checkbox';

const Widget = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '$4',
  border: '10px solid transparent',
  variants: {
    isActiveSelected: {
      true: {
        borderColor: '$purple9',
      },
    },
  },
});

const WidgetNav = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '$3',
  padding: '$4',
});

export function Filtered(props: {
  children: (activeIdx: number) => React.ReactNode;
  name?: string;
  data: unknown[];
  onChangeSelection?: (items: any[]) => void;
}) {
  const { children, name, data, onChangeSelection } = props;

  const selectable = onChangeSelection && data.length > 1;

  const [activeIdx, setActiveIdx] = useState(0);
  const [selectedItems, setSelectedItems] = useState<unknown[]>([]);

  const isActiveSelected = selectedItems.some(s => isEqual(s, data[activeIdx]));

  const onSelect = (items: any[]) => {
    if (onChangeSelection) {
      setSelectedItems(items);
      onChangeSelection(items);
    }
  };
  const toggleActive = () => {
    if (!selectable) {
      return;
    }
    if (isActiveSelected) {
      onSelect(selectedItems.filter(s => !isEqual(s, data[activeIdx])));
    } else {
      onSelect([...selectedItems, data[activeIdx]]);
    }
  };

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        setActiveIdx(Math.max(0, activeIdx - 1));
      }
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        setActiveIdx(Math.min(data.length - 1, activeIdx + 1));
      }
      if (e.key === 't' || e.key === ' ') {
        toggleActive();
      }
    }

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [toggleActive]);

  return (
    <Widget tabIndex={0} isActiveSelected={isActiveSelected} id="widget-container">
      <Pagination
        itemCount={data.length}
        pageSize={1}
        currentPage={activeIdx + 1}
        onChange={nextIdx => {
          setActiveIdx(nextIdx - 1);
        }}
      />
      {children(activeIdx)}
      {selectable && (
        <WidgetNav>
          <Button icon="select_all" text="Select all" onClick={() => onSelect(data)} weight="outlined" size="small" />
          <Button
            icon="clear"
            text="Select none"
            onClick={() => onSelect([])}
            weight="outlined"
            size="small"
            css={{ marginRight: '$5' }}
          />
          <Checkbox
            id={`selected-${name}`}
            label={isActiveSelected ? `Selected ${name}` : `Select ${name}`}
            isChecked={isActiveSelected}
            onCheckedChange={toggleActive}
          />
        </WidgetNav>
      )}
    </Widget>
  );
}
